import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { CancelBookingModal } from 'us-react-components/dist/CancelBookingModal/CancelBookingModal';
import { DeclineBookingModal } from 'us-react-components/dist/DeclineBookingModal/DeclineBookingModal';
import { FamilyPaywallModal } from 'us-react-components/dist/FamilyPaywallModal/FamilyPaywallModal';
import { SwitchPaymentModal } from 'us-react-components/dist/SwitchPaymentModal/SwitchPaymentModal';
import {
  Booking,
  BookingService,
  BookingStatus,
  chargedPaymentStatuses,
  SitterService,
} from 'us-web-services';
import {
  formatDateRange,
  getTimeOfDayString,
} from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { checkForPaywall } from '../../utils/PaywallUtil';

// Mirrors apps/angular/src/js/ng/common/resources/us-booking.resource.js:128
// `containsAction` — parentActions / sitterActions are arrays of objects
// keyed by `.code`, not plain strings.
const containsAction = (actions: BookingStatus[] | undefined, code: string): boolean =>
  !!actions?.some((a) => a.code === code);

const CAREGIVER_TYPE_AGENCY = 2;

const sentenceCase = (s?: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

interface BookingIncludesItem {
  type?: string;
  label?: string;
}

interface BookingWithExtras extends Booking {
  title?: string;
  subTitle?: string;
  groupId?: number;
  group?: { label?: string };
  caregiverNote?: string;
  kidsAges?: string;
  hasDog?: boolean;
  hasCat?: boolean;
  hasOtherPet?: boolean;
  paymentFrequency?: { id?: number };
  interviewType?: string;
  includes?: BookingIncludesItem[];
  sweeteners?: BookingIncludesItem[];
  description?: string;
  type?: string;
  bookingTypeCode?: string;
}

const getMapLink = (addr?: {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}): string => {
  if (!addr) return '';
  const q = encodeURIComponent(
    [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
  );
  return `https://maps.google.com/?q=${q}`;
};

const BookingDetailsCard = ({
  booking,
  isParent,
  t,
}: {
  booking: BookingWithExtras;
  isParent: boolean;
  t: TFunction;
}): JSX.Element => {
  const code = booking.bookingTypeCode ?? 'childCare';
  const isChildCare = code === 'childCare';
  const isPetCare = code === 'petCare';
  const isSeniorCare = code === 'seniorCare';
  const isHousehold = code === 'householdServices';
  const numKids = booking.numberOfChildren ?? 0;
  const status = booking.status?.code ?? '';
  const isInterview = booking.type === 'interview';
  const kidWord = (): string => {
    if (isChildCare) return numKids === 1 ? 'child' : 'children';
    if (isPetCare) return numKids === 1 ? 'pet' : 'pets';
    if (isSeniorCare) return numKids === 1 ? 'senior' : 'seniors';
    return '';
  };
  const paymentLabel = booking.paymentType === 'credit'
    ? t('pages.bookingDetail.creditCard')
    : booking.paymentType === 'cash'
      ? t('pages.bookingDetail.cashOrCheck')
      : '';
  const phone = isParent ? booking.caregiver?.phone : booking.parent?.phone;

  return (
    <div id='booking-details' className='us-card rounded p-5 mb-4'>
      <h3 className='font-semibold mb-3 capitalize'>
        {t('pages.bookingDetail.detailsHeading', { type: booking.type ?? 'Booking' })}
      </h3>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          {isInterview && booking.interviewType && (
            <div className='mb-2'>
              <b>{t('pages.bookingDetail.typeLabel')}</b>{' '}
              {booking.interviewType}{t('pages.bookingDetail.interviewSuffix')}
              {booking.interviewType === 'phone' && isParent && (
                <>{t('pages.bookingDetail.youWillCall', { name: booking.caregiver?.firstName })}</>
              )}
            </div>
          )}

          <div className='mb-2'>
            <b>{t('pages.bookingDetail.careForLabel')}</b>{' '}
            {isHousehold ? (
              <>{t('pages.bookingDetail.householdOnly')}</>
            ) : (
              <>
                {numKids} {kidWord()}
              </>
            )}
            {isChildCare &&
              Array.from({ length: numKids }).map((_, i) => (
                <i
                  key={i}
                  className='fa-solid fa-face-smile text-sapphire ml-2 align-middle'
                />
              ))}
            {isPetCare && (
              <span className='ml-2'>
                {booking.hasCat && <i className='fa-solid fa-cat mr-1' />}
                {booking.hasDog && <i className='fa-solid fa-dog mr-1' />}
                {booking.hasOtherPet && <i className='fa-solid fa-paw mr-1' />}
              </span>
            )}
          </div>

          {booking.kidsAges && !isPetCare && !isHousehold && (
            <div className='mb-2'>
              <b>{numKids === 1 ? t('pages.bookingDetail.ageLabel') : t('pages.bookingDetail.agesLabel')}</b>{' '}
              {booking.kidsAges}
            </div>
          )}

          {!isInterview || booking.interviewType !== 'phone' ? (
            <div className='mb-2'>
              <b>{t('pages.bookingDetail.rateLabel')}</b> ${booking.rate}
              {booking.paymentFrequency?.id === 2
                ? `${t('pages.bookingDetail.flatForSuffix')}${booking.type}`
                : t('pages.bookingDetail.perHourSuffix')}
            </div>
          ) : null}

          {booking.rate != null && (
            <div className='mb-2'>
              <b>{t('pages.bookingDetail.paymentTypeLabel')}</b> {paymentLabel}
            </div>
          )}

          {booking.address && (booking.type === 'job' || booking.interviewType !== 'phone') && (
            <>
              {booking.address.neighborhood && (
                <div className='mb-2'>
                  <b>{t('pages.bookingDetail.neighborhoodLabel')}</b> {booking.address.neighborhood}
                </div>
              )}
              <div className='mb-2'>
                <b>{t('pages.bookingDetail.locationLabel')}</b>
                <a
                  id='booking-address-link'
                  target='_blank'
                  rel='noreferrer'
                  href={getMapLink(booking.address)}
                  className='block underline'
                >
                  {booking.address.street && (
                    <>
                      {booking.address.street}
                      <br />
                    </>
                  )}
                  {booking.address.street2 && (
                    <>
                      {booking.address.street2}
                      <br />
                    </>
                  )}
                  {booking.address.city}, {booking.address.state} {booking.address.zip}
                </a>
              </div>
            </>
          )}

          {!status.includes('reject') && (
            <div className='mb-2'>
              <b>{isParent ? t('pages.bookingDetail.caregiverPhone') : t('pages.bookingDetail.familyPhone')}</b>{' '}
              {status === 'pending' ? (
                <i>{t('pages.bookingDetail.shownIfAccepted')}</i>
              ) : (
                <span>{phone ?? <i>{t('pages.bookingDetail.notProvided')}</i>}</span>
              )}
            </div>
          )}
        </div>
        <div>
          {isChildCare && booking.includes && booking.includes.length > 0 && (
            <div className='mb-3'>
              <h4 className='font-semibold mb-1'>{t('pages.bookingDetail.jobIncludes')}</h4>
              <ul className='list-disc pl-5'>
                {booking.includes.map((inc) => (
                  <li key={inc.type} id={`include-${inc.type}`}>
                    {inc.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isChildCare && booking.sweeteners && booking.sweeteners.length > 0 && (
            <div className='mb-3'>
              <h4 className='font-semibold mb-1'>{t('pages.bookingDetail.sweetenTheDeal')}</h4>
              <ul className='list-disc pl-5'>
                {booking.sweeteners.map((s) => (
                  <li key={s.type} id={`sweetener-${s.type}`}>
                    {s.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {booking.description && (
          <div className='col-span-full'>
            <h4 className='font-semibold mb-1'>{t('pages.bookingDetail.jobDescription')}</h4>
            <div className='bg-sapphire-50 p-3 rounded'>{booking.description}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export const BookingDetail = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { tracker, config } } = useDependencies();
  const trackBookingAction = (action: string): void => {
    tracker.track('clicked-button', { action, component: 'Booking Detail' });
  };
  const user = stores.user.user;

  type LoadState = 'loading' | 'loaded' | 'not-found' | 'forbidden' | 'error';
  const [booking, setBooking] = useState<BookingWithExtras | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [switchPaymentOpen, setSwitchPaymentOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [sitterAvailabilities, setSitterAvailabilities] = useState<
    Array<{ start?: string; end?: string; booking?: { parent?: { pictureUrl?: string; firstName?: string } } }>
  >([]);


  useEffect(() => {
    document.title = 'UrbanSitter - Booking';
  }, []);

  useEffect(() => {
    const bookingId = Number(pathId);
    if (!bookingId || !user?.id) return;
    const controller = new AbortController();
    setLoadState('loading');
    const load = async (): Promise<void> => {
      try {
        const resp = await BookingService.get(bookingId, { params: { userId: user.id }, signal: controller.signal });
        setBooking((resp.data?.data ?? null) as BookingWithExtras | null);
        setLoadState('loaded');
      } catch (err) {
        const e = err as { code?: string; response?: { status?: number } };
        if (e?.code === 'ERR_CANCELED') return;
        const status = e?.response?.status;
        if (status === 404) setLoadState('not-found');
        else if (status === 403) setLoadState('forbidden');
        else setLoadState('error');
      }
    };
    void load();
    return (): void => controller.abort();
  }, [pathId, user?.id, loadAttempt]);

  useEffect(() => {
    if (!booking || !user) return undefined;
    const isParentUser = !!user.parent;
    const bookingStatus = booking.status?.code ?? '';
    const sitterId = (booking as { sitter?: { id?: number } }).sitter?.id ?? booking.caregiver?.id;
    if (isParentUser || bookingStatus !== 'pending' || !sitterId || !booking.start) return undefined;
    const startDate = new Date(booking.start);
    const dayStart = new Date(startDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(startDate);
    dayEnd.setHours(23, 59, 59, 999);
    const controller = new AbortController();
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.getSchedules({
          params: {
            sitterId,
            start: dayStart.toISOString(),
            end: dayEnd.toISOString(),
          },
          signal: controller.signal,
        });
        setSitterAvailabilities((resp.data?.data ?? []) as typeof sitterAvailabilities);
      } catch {
        /* ignore */
      }
    };
    void load();
    return (): void => controller.abort();
  }, [booking?.id, booking?.status?.code, user?.id]);

  if (loadState === 'loading') {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('pages.bookingDetail.loading')}</span>
      </div>
    );
  }

  if (loadState === 'not-found' || loadState === 'forbidden' || loadState === 'error') {
    const titleKey = loadState === 'not-found'
      ? 'pages.bookingDetail.notFound'
      : loadState === 'forbidden'
        ? 'pages.bookingDetail.forbidden'
        : 'pages.bookingDetail.loadFailed';
    const bodyKey = loadState === 'not-found'
      ? 'pages.bookingDetail.notFoundBody'
      : loadState === 'forbidden'
        ? 'pages.bookingDetail.forbiddenBody'
        : 'pages.bookingDetail.loadFailedBody';
    return (
      <div className='container py-8'>
        <Button
          buttonType={ButtonType.tertiary}
          onClick={(): void => navigate('/bookings')}
          className='text-sm underline cursor-pointer bg-transparent border-0 p-0 mb-4'
        >
          {t('pages.bookingDetail.backToList')}
        </Button>
        <div className='us-card rounded p-5'>
          <h2 className='text-xl font-semibold mb-2'>{t(titleKey)}</h2>
          <p className='mb-4'>{t(bodyKey)}</p>
          {loadState === 'error' && (
            <Button
              buttonType={ButtonType.primary}
              onClick={(): void => setLoadAttempt((n) => n + 1)}
            >
              {t('pages.bookingDetail.retry')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('pages.bookingDetail.loading')}</span>
      </div>
    );
  }

  const isParent = !!user?.parent;
  const contact = isParent ? booking.caregiver : booking.parent;
  const contactRoute = isParent ? 'sitter' : 'parent';
  const isAgency = booking.caregiverType === CAREGIVER_TYPE_AGENCY;
  const status = booking.status?.code ?? '';
  const statusType = booking.status?.type ?? '';
  const parentActions = booking.status?.parentActions;
  const rejectedReason = (booking as { rejectedReason?: string }).rejectedReason;
  const hasBeenReviewed =
    (booking as { hasBeenReviewed?: boolean; reviewed?: boolean }).hasBeenReviewed ??
    (booking as { reviewed?: boolean }).reviewed ??
    false;
  // Mirrors parent-booking-summary.js:43 canReview()
  const canReview =
    isParent &&
    !isAgency &&
    !hasBeenReviewed &&
    (containsAction(parentActions, 'review') ||
      status === 'sitter_cancel' ||
      (status === 'parent_cancel' && rejectedReason === 'sitter_canceled'));
  const canAcceptReschedule = isParent && containsAction(parentActions, 'accepted');
  const canPayNow = isParent && containsAction(parentActions, 'parent_confirm_payment');
  const canCancelAsParent = isParent && containsAction(parentActions, 'parent_cancel');
  const canRescheduleAsParent = isParent && containsAction(parentActions, 'parent_reschedule');
  const canChangePayment =
    !booking.status?.code || !chargedPaymentStatuses.has(booking.status.code);
  // Angular sitter-booking-summary.tpl.html: Cancel only for accepted or
  // sitter_reschedule, NOT pending (pending shows Decline instead).
  const canCancelAsSitter = !isParent && (status === 'accepted' || status === 'sitter_reschedule');
  // Angular sitter-booking-summary.tpl.html: Message hidden for sitters while pending.
  const showMessage = isParent ? !isAgency : status !== 'pending' && !!booking.parent?.active;
  const showFindAvailable = isParent && statusType === 'reject_active';
  const showUpdateCard =
    isParent && !!booking.subTitle && booking.subTitle.toLowerCase().includes('update');
  const bookingSitterId = booking.caregiver?.id ?? booking.sitter?.id;

  const acceptReschedule = async (): Promise<void> => {
    if (!booking.id || !user?.id) return;
    setAccepting(true);
    try {
      const resp = await BookingService.patch(
        booking.id,
        { id: booking.id, status: { code: 'accepted' } } as Booking,
        { params: { userId: user.id } },
      );
      const updated = (resp.data?.data ?? null) as BookingWithExtras | null;
      if (updated) setBooking(updated);
    } finally {
      setAccepting(false);
    }
  };

  // Sitter accepting a pending booking (Angular sitter-booking.js acceptBooking).
  const acceptBooking = async (): Promise<void> => {
    if (!booking.id || !user?.id) return;
    setAccepting(true);
    try {
      const resp = await BookingService.patch(
        booking.id,
        { id: booking.id, status: { code: 'accepted' } } as Booking,
        { params: { userId: user.id } },
      );
      const updated = (resp.data?.data ?? null) as BookingWithExtras | null;
      if (updated) setBooking(updated);
    } finally {
      setAccepting(false);
    }
  };

  const goToMessaging = (): void => {
    if (user?.id && bookingSitterId) {
      navigate(`/app/parent/${user.id}/messages/${bookingSitterId}`);
    }
  };

  const showSitterPendingActions = !isParent && status === 'pending';
  const showAvailabilitySidebar = showSitterPendingActions && sitterAvailabilities.length > 0;

  return (
    <div className='container py-8'>
      <Button
        buttonType={ButtonType.tertiary}
        onClick={(): void => navigate('/bookings')}
        className='text-sm underline cursor-pointer bg-transparent border-0 p-0'
      >
        {t('pages.bookingDetail.backToList')}
      </Button>

      <h1 className='text-2xl font-semibold mt-3 mb-2'>
        {booking.title ?? formatDateRange(booking.start, booking.end, 'long')}
      </h1>
      <div className='mb-4'>
        {booking.subTitle ?? booking.status?.label}
      </div>

      {booking.group?.label && (
        <a
          id='booking-group-summary'
          href={`/app/booking-group/${booking.groupId}/summary`}
          className='block mb-2 underline'
        >
          {booking.group.label}
        </a>
      )}

      <div className={`grid gap-4 ${showAvailabilitySidebar ? 'md:grid-cols-12' : ''}`}>
        <div className={showAvailabilitySidebar ? 'md:col-span-9' : ''}>
          {/* Summary card — photo, date, non-pending CTAs */}
          <div className='us-card rounded p-5 mb-4'>
            <div className='flex items-start gap-4 mb-4'>
              {contact?.pictureUrl && (
                <img
                  src={contact.pictureUrl}
                  alt={contact.name ?? ''}
                  className='w-20 h-20 rounded-full object-cover flex-shrink-0'
                />
              )}
              <div>
                <h3 className='font-semibold text-lg'>
                  <a href={`/app/${contactRoute}/${contact?.id}`} className='underline'>
                    {sentenceCase(contact?.name)}
                  </a>
                </h3>
                <div>{formatDateRange(booking.start, booking.end, 'long')}</div>
                {booking.address?.city && (
                  <div className='text-sm text-neutral-600 mt-1'>
                    {booking.address.city}
                    {booking.address.state ? `, ${booking.address.state}` : ''}
                  </div>
                )}
                {booking.rate && (
                  <div className='text-sm mt-1'>${booking.rate}/hr</div>
                )}
              </div>
            </div>

            {isAgency && booking.caregiverNote && (
              <div className='mb-4'>
                <div className='font-semibold mb-1'>
                  {t('pages.bookingDetail.noteHeading', {
                    name: sentenceCase(booking.caregiver?.firstName),
                  })}
                </div>
                <p>{booking.caregiverNote}</p>
              </div>
            )}

            {/* CTAs — parent actions and non-pending sitter actions */}
            <div className='flex flex-wrap gap-2 mt-4 mb-0'>
              {canAcceptReschedule && (
                <Button
                  id='accept-reschedule'
                  buttonType={ButtonType.primary}
                  disabled={accepting}
                  onClick={(): void => {
                    trackBookingAction('Accept Reschedule CTA');
                    void acceptReschedule();
                  }}
                >
                  {t('pages.bookingDetail.acceptReschedule')}
                </Button>
              )}
              {showMessage && (
                <Button
                  id='message-sitter'
                  buttonType={ButtonType.secondary}
                  onClick={(): void => {
                    tracker.track('booking-details', {
                      action: 'message-sitter',
                      paywallTrigger: 'message caregiver from booking summary',
                    });
                    if (isParent) {
                      void checkForPaywall({
                        userId: user?.id,
                        sitter: (booking.caregiver ?? {}) as { relationship?: unknown },
                        action: goToMessaging,
                        openPaywall: (): void => setPaywallOpen(true),
                      });
                    } else {
                      const sitterId = (booking as { sitter?: { id?: number } }).sitter?.id;
                      const parentId = booking.parent?.id;
                      if (sitterId && parentId) navigate(`/sitter/${sitterId}/messages/${parentId}`);
                    }
                  }}
                >
                  {t('pages.bookingDetail.message')}
                </Button>
              )}
              {canPayNow && (
                <Button
                  id='confirm-payment'
                  buttonType={ButtonType.primary}
                  onClick={(): void => {
                    trackBookingAction('Pay Now CTA');
                    navigate(`/bookings/${booking.id}/confirm-payment`);
                  }}
                >
                  {t('pages.bookingDetail.payNow')}
                </Button>
              )}
              {canReview && (
                <Button
                  id='review-sitter'
                  buttonType={ButtonType.secondary}
                  onClick={(): void => {
                    tracker.track('booking-details', { action: 'review-caregiver' });
                    if (bookingSitterId) navigate(`/sitter/${bookingSitterId}/review`);
                  }}
                >
                  {t('pages.bookingDetail.reviewCaregiver')}
                </Button>
              )}
              {showFindAvailable && (
                <Button
                  id='find-sitters'
                  buttonType={ButtonType.secondary}
                  onClick={(): void => {
                    tracker.track('booking-details', { action: 'find-available-sitters' });
                    const params = new URLSearchParams();
                    if (booking.start) params.set('start', booking.start);
                    if (booking.end) params.set('end', booking.end);
                    const qs = params.toString();
                    navigate(qs ? `/sitters?${qs}` : '/sitters');
                  }}
                >
                  {t('pages.bookingDetail.findAvailableSitters')}
                </Button>
              )}
              {showUpdateCard && (
                <Button
                  id='update-credit-card'
                  buttonType={ButtonType.secondary}
                  onClick={(): void => {
                    tracker.track('booking-details', { action: 'update-credit-card' });
                    if (user?.id) navigate(`/user/${user.id}/credit-card`);
                  }}
                >
                  {t('pages.bookingDetail.updateCreditCard')}
                </Button>
              )}
              {canRescheduleAsParent && (
                <Button
                  id='reschedule-booking'
                  buttonType={ButtonType.secondary}
                  onClick={(): void => {
                    trackBookingAction('Reschedule CTA');
                    navigate(`/bookings/${booking.id}/reschedule`);
                  }}
                >
                  {t('pages.bookingDetail.reschedule')}
                </Button>
              )}
              {!isParent && status === 'job_finished' && booking.paymentType === 'credit' && (
                <Button
                  buttonType={ButtonType.primary}
                  onClick={(): void => {
                    trackBookingAction('Confirm Hours CTA');
                    navigate(`/bookings/${booking.id}/confirm-hours`);
                  }}
                >
                  {t('pages.bookingDetail.confirmHours')}
                </Button>
              )}
              {(canCancelAsParent || canCancelAsSitter) && (
                <Button
                  id='cancel-booking'
                  buttonType={ButtonType.secondary}
                  onClick={(): void => {
                    trackBookingAction('Cancel CTA');
                    setCancelOpen(true);
                  }}
                >
                  {t('pages.bookingDetail.cancel', { type: booking.type ?? 'booking' })}
                </Button>
              )}
              {isParent && booking.paymentType && statusType === 'pending' && canChangePayment && (
                <Button
                  buttonType={ButtonType.secondary}
                  onClick={(): void => {
                    trackBookingAction('Change Payment CTA');
                    setSwitchPaymentOpen(true);
                  }}
                >
                  {t('pages.bookingDetail.changePayment')}
                </Button>
              )}
            </div>
          </div>

          <BookingDetailsCard booking={booking} isParent={isParent} t={t} />

          {/* Sitter pending section — below booking details per Angular sitter-booking.tpl.html */}
          {showSitterPendingActions && (
            <div className='us-card rounded p-5 mb-4'>
              <p className='mb-3 italic'>
                &ldquo;Accept&rdquo; means you accept all the job details.
              </p>
              <div className='flex gap-3'>
                <Button
                  id='decline-booking'
                  buttonType={ButtonType.secondary}
                  onClick={(): void => {
                    trackBookingAction('Decline CTA');
                    setDeclineOpen(true);
                  }}
                >
                  {t('pages.bookingDetail.decline')}
                </Button>
                <Button
                  id='accept-pending'
                  buttonType={ButtonType.primary}
                  disabled={accepting}
                  onClick={(): void => {
                    trackBookingAction('Accept CTA');
                    void acceptBooking();
                  }}
                >
                  Accept
                </Button>
              </div>
            </div>
          )}

          <div className='mb-4'>
            <div className='font-semibold mb-1'>{t('pages.bookingDetail.reliabilityHeading')}</div>
            <p>
              {t('pages.bookingDetail.reliabilityBody')}{' '}
              <a
                id='reliability-policy'
                target='_blank'
                rel='noreferrer'
                href='https://urbansitter.zendesk.com/hc/en-us/articles/200827526-Reliability-Policy'
                className='underline'
              >
                {t('pages.bookingDetail.readMore')}
              </a>
              .
            </p>
          </div>
        </div>

        {/* Availability sidebar — shown for sitter on pending bookings */}
        {showAvailabilitySidebar && (
          <aside id='calendar-side-panel' className='md:col-span-3'>
            <div className='space-y-2'>
              {sitterAvailabilities.map((avail, i) => (
                <div key={i} className='us-card rounded p-3 text-sm'>
                  {avail.start && (
                    <div className='font-semibold'>
                      {getTimeOfDayString(avail.start)}
                      {avail.end && (
                        <>
                          {' – '}
                          {getTimeOfDayString(avail.end)}
                        </>
                      )}
                    </div>
                  )}
                  {avail.booking ? (
                    <div className='flex items-center gap-2 mt-1'>
                      {avail.booking.parent?.pictureUrl && (
                        <img
                          src={avail.booking.parent.pictureUrl}
                          alt=''
                          className='w-6 h-6 rounded-full object-cover'
                        />
                      )}
                      <span>{avail.booking.parent?.firstName}</span>
                    </div>
                  ) : (
                    <div className='text-green-700 mt-1'>Available</div>
                  )}
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>

      <CancelBookingModal
        isOpen={cancelOpen}
        onClose={(): void => setCancelOpen(false)}
        booking={booking}
        isSitter={!isParent}
        onCancelled={(): void => navigate('/bookings')}
      />
      <DeclineBookingModal
        isOpen={declineOpen}
        onClose={(): void => setDeclineOpen(false)}
        booking={booking}
        onDeclined={(): void => navigate('/bookings')}
      />
      <SwitchPaymentModal
        isOpen={switchPaymentOpen}
        onClose={(): void => setSwitchPaymentOpen(false)}
        booking={booking}
        onSwitched={(): void => {
          if (booking.id && user?.id) {
            void (async (): Promise<void> => {
              try {
                const resp = await BookingService.get(booking.id!, { params: { userId: user.id } });
                setBooking((resp.data?.data ?? null) as BookingWithExtras | null);
              } catch {
                /* ignore */
              }
            })();
          }
        }}
      />
      {user?.id && booking.caregiver && (
        <FamilyPaywallModal
          showModal={paywallOpen}
          onClose={(): void => setPaywallOpen(false)}
          onSuccess={goToMessaging}
          sitter={booking.caregiver}
          userId={user.id}
          stripeKey={config.STRIPE_KEY}
          recaptchaKey={config.RECAPTCHA_KEY}
        />
      )}
    </div>
  );
});
